/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';

import { IProduct } from '@/interfaces/product';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  notification,
  Radio,
  Row,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import { editProduct } from '@/adapters/product';
import UnitSelector from '@/components/selector/unit-selector';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';

interface IProps {
  data: IProduct;
  onClose: () => void;
}

const ProductEditInfo = ({ data, onClose }: IProps) => {
  const [form] = Form.useForm<Partial<IProduct>>();
  const mutations = useMutation({
    mutationFn: async (data: Partial<IProduct>) => await editProduct(data),
    mutationKey: ['actionProduct'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'อัพเดทสินค้าสำเร็จ',
        });
        onReset();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<IProduct>) => {
        values.id = Number(data?.id || 0);
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    if (data?.id) {
      form.setFieldsValue({
        ...data,
      });
      // if (data?.image) {
      //   setImage(data?.image);
      // }
    }
  }, [data?.id, data, form]);

  const packageUnitId = Form.useWatch('packageUnitId', form);
  const baseUnitId = Form.useWatch('baseUnitId', form);
  const _businessUnitId = Form.useWatch('businessUnitId', form);
  return (
    <div className="flex w-full">
      <Form
        form={form}
        name="product-edit"
        onFinish={handleUpdate}
        layout="vertical"
        className="w-full"
      >
        <Row gutter={24}>
          <Col span={24} md={8}>
            <Form.Item
              label="รหัสสินค้า"
              name="code"
              rules={[{ required: true, message: 'ระบุรหัสสินค้า' }]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
          <Col span={24} md={16}>
            <Form.Item
              label="ชื่อสินค้า"
              name="name"
              rules={[{ required: true, message: 'ระบุชื่อสินค้า' }]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
          <Col span={24} md={8}>
            <Form.Item
              label="หน่วย Package"
              name="packageUnitId"
              rules={[{ required: true, message: 'เลือกหน่วย Package' }]}
            >
              <UnitSelector
                value={packageUnitId}
                size="large"
                handleSelectData={e => form.setFieldValue('packageUnitId', e)}
              />
            </Form.Item>
          </Col>
          <Col span={24} md={8}>
            <Form.Item
              label="Conversion"
              name="conversion"
              rules={[{ required: true, message: 'ระบุConversion' }]}
            >
              <InputNumber size="large" className="w-full" min={1} />
            </Form.Item>
          </Col>
          <Col span={24} md={8}>
            <Form.Item
              label="หน่วยขาย"
              name="baseUnitId"
              rules={[{ required: true, message: 'เลือกหน่วยขาย' }]}
            >
              <UnitSelector
                value={baseUnitId}
                size="large"
                handleSelectData={e => form.setFieldValue('baseUnitId', e)}
              />
            </Form.Item>
          </Col>
          {/* <Col span={24} md={8}>
            <Form.Item
              label="ต้นทุน/หน่วย"
              name="cost"
              rules={[{ required: true, message: 'ระบุต้นทุน' }]}
            >
              <InputNumber size="large" className="w-full" />
            </Form.Item>
          </Col> */}
          <Col span={24} md={8}>
            <Form.Item
              label={data?.iSample ? `ต้นทุน` : `ราคาขาย/หน่วย`}
              name="unitPrice"
              rules={[{ required: true, message: 'ระบุราคาขาย' }]}
            >
              <InputNumber size="large" className="w-full" />
            </Form.Item>
          </Col>
          <Col span={24} md={8}>
            <Form.Item<IProduct>
              label="Safety Stock"
              name="safetyStock"
              rules={[{ required: true, message: 'ระบุSafety Stock' }]}
            >
              <InputNumber size="large" className="w-full" />
            </Form.Item>
          </Col>
          <Col span={24} md={8}>
            <Form.Item<IProduct> label="BU" name="businessUnitId">
              <BusinessUnitSelector
                handleSelectData={id => {
                  form.setFieldsValue({
                    businessUnitId: id,
                  });
                }}
                value={_businessUnitId}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="สถานะ"
              name="isActive"
              rules={[{ required: true, message: 'เลือกสถานะ' }]}
              initialValue={true}
            >
              <Radio.Group>
                <Radio value={true}>ใช้งาน</Radio>
                <Radio value={false}>ไม่ใช้งาน</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="หมายเหตุ" name="remark">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
        <div className="flex justify-end gap-2">
          <Button key="back" onClick={() => onClose()}>
            ยกเลิก
          </Button>
          <Button
            key="submit"
            type="primary"
            form="productGroup"
            loading={mutations.isPending}
            onClick={handleUpdate}
          >
            บันทึก
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ProductEditInfo;
